import type { QueryClient } from '@tanstack/react-query';
import { getConfig } from '../../config/runtimeConfig';
import { HttpError } from '../../lib/httpError';

/**
 * The study head (GET /studies/:studyId), as the viewer page and the details
 * editor read it: one cache entry per study, so a save in the editor and the
 * viewer's bar agree. Each page types the fields it uses.
 */
export const studyHeadKey = (studyId: string | undefined) => ['study-head', studyId] as const;

export async function fetchStudyHead<T>(studyId: string): Promise<T | null> {
  const res = await fetch(`${getConfig().apiRoot}/studies/${encodeURIComponent(studyId)}`, { credentials: 'include' });
  // null means "not yours" (404, or 403). Anything else (an expired session, a
  // 500, a rate limit) is an error, not an answer.
  if (res.status === 404 || res.status === 403) return null;
  if (!res.ok) throw new HttpError(`Request failed (${res.status})`, res.status);
  return (await res.json()) as T;
}

/**
 * Ask for the head of the study `path` opens (the viewer's `/studies/:id`),
 * into `qc`, at boot: alongside the session check and the edition rather than
 * after them. The page asks for it only once it knows the person is signed in,
 * which on a page load (a reload, a link from outside) waited for boot's
 * session answer and the first render: a whole round trip before the lists
 * could go out. The page then finds it in the cache, or in flight. Any other
 * path, an enterprise one (a study UID, not a public id) included, asks for
 * nothing. A guest's ask is refused and the page sends them to sign in, as
 * before; the 401 watcher is armed only after boot, so it never sees this one.
 */
export function prefetchStudyHead(path: string, qc: QueryClient): void {
  const studyId = /^\/studies\/(stu_[A-Za-z0-9]+)\/?$/.exec(path)?.[1];
  if (studyId) void qc.prefetchQuery({ queryKey: studyHeadKey(studyId), queryFn: () => fetchStudyHead(studyId) });
}
